import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  formatAgentDisplayName,
  parseSpecialistReport,
  summarizeSpecialistReport,
  type BackendArchitectReportShape,
  type BinderReportShape,
  type DesignerReportShape,
  type ParsedSpecialistReport,
  type RenderFixerReportShape,
  type WirerReportShape
} from "./mcp-chat-specialist-report-parser";
import { CheckIcon, ErrorIcon, SpinnerIcon } from "./mcp-tool-activity";

interface McpChatSpecialistReportProps {
  agent: string;
  content: string;
  isStreaming: boolean;
  /**
   * When true (chat turn still active), the accordion stays open. When the
   * turn ends this flips to false — the accordion respects the user override
   * if set, otherwise collapses to the default closed state.
   */
  keepOpen: boolean;
  /**
   * Rendering variant:
   * - "standalone" (default): full accordion wrapper with sparkle header + body.
   * - "inline": body-only markup, meant to be embedded inside an outer
   *   transcript group that already owns the accordion chrome.
   */
  variant?: "standalone" | "inline";
}

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`mcp-tool-transcript__chevron${open ? " mcp-tool-transcript__chevron--open" : ""}`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const SparkleIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
  </svg>
);

type Tone = "running" | "success" | "error";

function renderStatusIcon(tone: Tone): React.ReactNode {
  if (tone === "running") return <SpinnerIcon />;
  if (tone === "error") return <ErrorIcon />;
  return <CheckIcon />;
}

function getStatusIconClass(tone: Tone): string {
  if (tone === "running") return "mcp-tool-transcript__icon--running";
  if (tone === "error") return "mcp-tool-transcript__icon--error";
  return "mcp-tool-transcript__icon--success";
}

// User-facing in-progress phrase for each specialist. Kept short and
// action-oriented rather than referring to the internal "report" concept,
// which is an AI-to-AI artifact the user has no reason to care about.
function streamingLabel(agent: string): string {
  switch (agent) {
    case "designer":
      return "Designing your screen…";
    case "wirer":
      return "Wiring actions…";
    case "binder":
      return "Binding data…";
    case "backend-architect":
      return "Setting up data…";
    case "render-fixer":
      return "Polishing layout…";
    default:
      return "Working…";
  }
}

// ── Per-agent pretty body renderers ─────────────────────────────────────────

// Wirer/Binder bodies intentionally omit the per-item attribution lists.
// The header pill already carries the counts ("3 actions attached · 1 missing"),
// and rendering every successful attachment / parameter binding individually
// buried the important signals (missing/unbound/bounce-back) in a wall of text.
// Only problem signals and notes stay in the body.

const WirerBody: React.FC<{ data: WirerReportShape }> = ({ data }) => {
  const hasMissing = data.missingTriggers.length > 0 || data.missingActions.length > 0;
  if (!hasMissing && !data.bounceBack && !data.notes) return null;
  return (
    <div className="mcp-specialist-report__body">
      {data.missingTriggers.length > 0 && (
        <div className="mcp-specialist-report__section mcp-specialist-report__section--warn">
          <div className="mcp-specialist-report__section-title">Missing triggers · {data.missingTriggers.length}</div>
        </div>
      )}
      {data.missingActions.length > 0 && (
        <div className="mcp-specialist-report__section mcp-specialist-report__section--warn">
          <div className="mcp-specialist-report__section-title">Missing actions · {data.missingActions.length}</div>
        </div>
      )}
      {data.bounceBack && (
        <div className="mcp-specialist-report__bounceback">
          <strong>Bouncing back to {data.bounceBack.to}</strong>
          <span className="mcp-specialist-report__bounceback-reason">{data.bounceBack.reason}</span>
        </div>
      )}
      {data.notes && <p className="mcp-specialist-report__notes">{data.notes}</p>}
    </div>
  );
};

const BinderBody: React.FC<{ data: BinderReportShape }> = ({ data }) => {
  const hasUnbound = data.unboundParameters.length > 0;
  if (!hasUnbound && !data.bounceBack && !data.notes) return null;
  return (
    <div className="mcp-specialist-report__body">
      {hasUnbound && (
        <div className="mcp-specialist-report__section mcp-specialist-report__section--warn">
          <div className="mcp-specialist-report__section-title">
            Unbound parameters · {data.unboundParameters.length}
          </div>
        </div>
      )}
      {data.bounceBack && (
        <div className="mcp-specialist-report__bounceback">
          <strong>Bouncing back to {data.bounceBack.to}</strong>
          <span className="mcp-specialist-report__bounceback-reason">{data.bounceBack.reason}</span>
        </div>
      )}
      {data.notes && <p className="mcp-specialist-report__notes">{data.notes}</p>}
    </div>
  );
};

const DesignerBody: React.FC<{ data: DesignerReportShape }> = ({ data }) => (
  <div className="mcp-specialist-report__body">
    <div className="mcp-specialist-report__meta">
      {data.screenName && (
        <div className="mcp-specialist-report__meta-row">
          <span className="mcp-specialist-report__meta-label">Screen</span>
          <span className="mcp-specialist-report__meta-value">{data.screenName}</span>
        </div>
      )}
      <div className="mcp-specialist-report__meta-row">
        <span className="mcp-specialist-report__meta-label">Components</span>
        <span className="mcp-specialist-report__meta-value">{data.componentCount}</span>
      </div>
      <div className="mcp-specialist-report__meta-row">
        <span className="mcp-specialist-report__meta-label">Bound views</span>
        <span className="mcp-specialist-report__meta-value">{data.boundViewCount}</span>
      </div>
      {data.designMode && (
        <div className="mcp-specialist-report__meta-row">
          <span className="mcp-specialist-report__meta-label">Mode</span>
          <span className="mcp-specialist-report__chip">{data.designMode}</span>
        </div>
      )}
      <div className="mcp-specialist-report__meta-row">
        <span className="mcp-specialist-report__meta-label">Render</span>
        <span
          className={`mcp-specialist-report__chip ${
            data.renderSucceeded ? "mcp-specialist-report__chip--ok" : "mcp-specialist-report__chip--warn"
          }`}
        >
          {data.renderSucceeded ? "succeeded" : "failed"}
        </span>
      </div>
    </div>
    {data.referencedActions.length > 0 && (
      <div className="mcp-specialist-report__section">
        <div className="mcp-specialist-report__section-title">Referenced actions</div>
        <div className="mcp-specialist-report__chips">
          {data.referencedActions.map((a, i) => (
            <span key={i} className="mcp-specialist-report__chip">
              {a}
            </span>
          ))}
        </div>
      </div>
    )}
    {data.notes && <p className="mcp-specialist-report__notes">{data.notes}</p>}
  </div>
);

const BackendArchitectBody: React.FC<{ data: BackendArchitectReportShape }> = ({ data }) => (
  <div className="mcp-specialist-report__body">
    <div className="mcp-specialist-report__meta">
      {data.schemaName && (
        <div className="mcp-specialist-report__meta-row">
          <span className="mcp-specialist-report__meta-label">Schema</span>
          <span className="mcp-specialist-report__meta-value">{data.schemaName}</span>
        </div>
      )}
      {data.datasourceId && (
        <div className="mcp-specialist-report__meta-row">
          <span className="mcp-specialist-report__meta-label">Datasource</span>
          <span className="mcp-specialist-report__meta-value mcp-specialist-report__meta-value--mono">
            {data.datasourceId}
          </span>
        </div>
      )}
    </div>
    {data.tablesCreated.length > 0 && (
      <div className="mcp-specialist-report__section">
        <div className="mcp-specialist-report__section-title">Tables created</div>
        <div className="mcp-specialist-report__chips">
          {data.tablesCreated.map((t, i) => (
            <span key={i} className="mcp-specialist-report__chip">
              {t}
            </span>
          ))}
        </div>
      </div>
    )}
    {data.actionsCreated.length > 0 && (
      <div className="mcp-specialist-report__section">
        <div className="mcp-specialist-report__section-title">Actions created</div>
        <div className="mcp-specialist-report__chips">
          {data.actionsCreated.map((a, i) => (
            <span key={i} className="mcp-specialist-report__chip">
              {a}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const RenderFixerBody: React.FC<{ data: RenderFixerReportShape }> = ({ data }) => (
  <div className="mcp-specialist-report__body">
    {data.appliedFixes.length > 0 && (
      <div className="mcp-specialist-report__section">
        <div className="mcp-specialist-report__section-title">Applied fixes</div>
        <ul className="mcp-specialist-report__bullet-list">
          {data.appliedFixes.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Specialist runs that don't emit a structured report are AI-internal rationale
// — not meant for the user. Rather than dumping the raw prose, surface a tiny
// neutral marker so the outer accordion still indicates the run completed.
const FallbackBody: React.FC = () => null;

function renderParsedBody(report: ParsedSpecialistReport): React.ReactNode {
  switch (report.kind) {
    case "wirer":
      return <WirerBody data={report.data} />;
    case "binder":
      return <BinderBody data={report.data} />;
    case "designer":
      return <DesignerBody data={report.data} />;
    case "backend-architect":
      return <BackendArchitectBody data={report.data} />;
    case "render-fixer":
      return <RenderFixerBody data={report.data} />;
    default:
      return null;
  }
}

// ── Main component ─────────────────────────────────────────────────────────

export const McpChatSpecialistReport: React.FC<McpChatSpecialistReportProps> = ({
  agent,
  content,
  isStreaming,
  keepOpen,
  variant = "standalone"
}) => {
  const parsed = isStreaming ? null : parseSpecialistReport(content, agent);
  const summary = parsed ? summarizeSpecialistReport(parsed) : null;
  const displayName = formatAgentDisplayName(agent);

  const body = parsed ? renderParsedBody(parsed) : <FallbackBody />;

  // Inline variant: body-only — the outer transcript group owns the accordion.
  if (variant === "inline") {
    if (isStreaming) {
      return (
        <div className="mcp-specialist-report__body-outer">
          <div className="mcp-specialist-report__streaming-note">
            <SpinnerIcon />
            <span>{streamingLabel(agent)}</span>
          </div>
        </div>
      );
    }
    return <div className="mcp-specialist-report__body-outer">{body}</div>;
  }

  // Tone: running while streaming, error if the summary flagged issues, success otherwise.
  const tone: Tone = isStreaming ? "running" : summary?.hasIssues ? "error" : "success";

  // Open/close state follows the same pattern as McpToolTranscript — forceOpen
  // while streaming OR while the chat turn is still active, with a user
  // override that resets whenever the force signal flips direction. A grace
  // period delays the auto-collapse so back-to-back specialist runs (Designer
  // → Wirer → Binder) don't flicker the panel between hand-offs; if a new run
  // starts within the window the timer cancels and the panel stays open.
  const COLLAPSE_GRACE_MS = 5000;
  const rawForceOpen = isStreaming || keepOpen;
  const [delayedForceOpen, setDelayedForceOpen] = useState(rawForceOpen);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (rawForceOpen) {
      if (collapseTimerRef.current != null) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      setDelayedForceOpen(true);
      return;
    }
    if (collapseTimerRef.current != null) return;
    collapseTimerRef.current = setTimeout(() => {
      collapseTimerRef.current = null;
      setDelayedForceOpen(false);
    }, COLLAPSE_GRACE_MS);
    return () => {
      if (collapseTimerRef.current != null) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [rawForceOpen]);

  const forceOpen = delayedForceOpen;
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  const expanded = userOverride != null ? userOverride : forceOpen;

  const lastForceRef = useRef(forceOpen);
  useEffect(() => {
    if (lastForceRef.current !== forceOpen) {
      setUserOverride(null);
      lastForceRef.current = forceOpen;
    }
  }, [forceOpen]);

  const toggle = useCallback(() => {
    setUserOverride((prev) => !(prev != null ? prev : forceOpen));
  }, [forceOpen]);

  // Header label:
  //   - streaming  → "Wirer is writing…"
  //   - completed  → "Wirer · 3 actions attached · 0 missing"
  const headerLabel = isStreaming
    ? `${displayName} is writing…`
    : summary
      ? `${displayName} · ${summary.headline}`
      : `${displayName} · done`;

  return (
    <div
      className={`mcp-tool-transcript mcp-tool-transcript--wrapper mcp-specialist-report mcp-tool-transcript--${expanded ? "open" : "closed"}`}
      data-tone={tone}
      data-agent={agent}
    >
      <button
        type="button"
        className="mcp-tool-transcript__wrapper-header mcp-specialist-report__header"
        aria-expanded={expanded}
        onClick={toggle}
      >
        <span className={`mcp-tool-transcript__icon ${getStatusIconClass(tone)}`}>
          {isStreaming ? renderStatusIcon(tone) : <SparkleIcon />}
        </span>
        <span className="mcp-tool-transcript__wrapper-label">{headerLabel}</span>
        <ChevronIcon open={expanded} />
      </button>

      <div
        className={`mcp-tool-transcript__wrapper-body${expanded ? " mcp-tool-transcript__wrapper-body--open" : ""}`}
        aria-hidden={!expanded}
      >
        <div className="mcp-tool-transcript__wrapper-body-inner mcp-specialist-report__body-outer">
          {isStreaming ? (
            <div className="mcp-specialist-report__streaming-note">
              <SpinnerIcon />
              <span>{streamingLabel(agent)}</span>
            </div>
          ) : (
            body
          )}
        </div>
      </div>
    </div>
  );
};
